import React from "react";
import BaseComponent, {executeComponentCallback} from "Core/components/BaseComponent";
import CreatableSelect from 'react-select/creatable';
import PropTypes from "prop-types";
import {map, omit, set, uniq} from 'lodash';
import {BUTTON_DISPLAY_TYPES, BUTTON_STYLE, BUTTON_STYLES} from "Core/components/display/Button";
import {INSERT_VALUE_BUTTON_TYPE, INSERT_VALUE_BUTTON_TYPES} from "Core/components/advanced/InsertValueButton/const";
import InsertValueButton from "../../advanced/InsertValueButton";
import {getArray, getBoolean, getNumber, getString, isset} from 'Core/helpers/data';
import {getDate, getDateString} from "Core/helpers/datetime";
import {getAppDatePluginLocale} from "Core/helpers/locale";
import YearDaySelectInputMenuComponent from "./Menu";
import {main_layout_element} from "Config/app";

class YearDaySelectInput extends BaseComponent {
	constructor(props) {
		super(props, {
			translationPath: 'YearDaySelectInput',
			domPrefix: 'year-day-select-input-component'
		});

		// Custom component methods
		this.getSelectedOption = this.getSelectedOption.bind(this);
		this.getLabel = this.getLabel.bind(this);
		this.onChange = this.onChange.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);

		// Insert value methods
		this.containsInsertValue = this.containsInsertValue.bind(this);
	}



	// Custom component methods -----------------------------------------------------------------------------------------
	/**
	 * Get value object from sting value
	 * @param {string|string[]} value - String or an array of strings if 'isMulti' prop is true.
	 * @return {Object} Value that can be used in 'react-select' component.
	 */
	getSelectedOption(value) {
		const {isMulti} = this.props;
		
		if (isMulti) {
			return (Array.isArray(value) ? value : getString(value).split(',')).map(v => {
				const value = v.trim();
				const label = this.getLabel(value);
				if (this.containsInsertValue(value)) return {label: value, value: value};
				else if (label) return {label: this.getLabel(value), value: value};
				return undefined;
			}).filter(i => isset(i));
		} else {
			if (!value) return null;
			const label = this.getLabel(value.trim());
			if (this.containsInsertValue(value.trim())) return {label: value.trim(), value: value.trim()};
			else if (label) return {label: this.getLabel(value.trim()), value: value.trim()};
			else return null;
		}
	}

	/**
	 * Get value label
	 * @param {string} value
	 */
	getLabel(value) {
		const {valueFormat, labelFormat, isLeapYear} = this.props;
		const year = (isLeapYear ? 2024 : 2025);
		
		return getDateString(getDate(`${year}-${value}`, `yyyy-${valueFormat}`), labelFormat, getAppDatePluginLocale());
	}

	/**
	 * 'react-select' onChange method
	 * @note Overwritten to support simple values.
	 *
	 * @param {Object} selectedValue - 'react-select' selected value.
	 */
	onChange(selectedValue) {
		const {isMulti, onChange} = this.props;
		if (!selectedValue) {
			executeComponentCallback(onChange, '');
		} else if (isMulti) {
			let selectedOptions = map(selectedValue, option => option.value);
			// Remove duplicates
			selectedOptions = uniq(selectedOptions);
			executeComponentCallback(onChange, selectedOptions);
		} else {
			executeComponentCallback(onChange, selectedValue.value);
		}
	}

	/**
	 * 'react-select' onChange method
	 * @note Overwritten to support Enter key event and better handle focus on Escape key event.
	 *
	 * @param {KeyboardEvent} event - Keydown event.
	 */
	onKeyDown(event) {
		const menuIsOpen = getBoolean(this, 'element.props.menuIsOpen');

		// Trigger standard keydown event
		executeComponentCallback(this.props.onKeyDown, event);

		// Trigger Enter key event if menu is closed since when it is opened Enter key is used to select a value.
		if (!menuIsOpen && event.key === 'Enter') executeComponentCallback(this.props.onEnterKey, event);

		// Stop propagation of the Escape key if menu is opened
		// @description This is done to prevent parent elements from catching the Escape key down while menu is opened
		// because we explicitly want Escape key to just close the menu. For example, this is necessary to prevent closing
		// a dialog, that closes on Escape key press, when Escape key is pressed on a focused input.
		if (menuIsOpen && event.key === 'Escape') {
			event.stopPropagation();
			event.nativeEvent.stopImmediatePropagation();
		}
		// Stop propagation of the Escape key and blur the element if it is focused
		else if (!menuIsOpen && event.key === 'Escape') {
			// Stop propagation of the Escape key if menu is opened
			if (document.activeElement === document.querySelector(`#${this.getDomId()}-input`)) {
				event.stopPropagation();
				event.nativeEvent.stopImmediatePropagation();
				if (!!this.element) this.element.blur();
			}
		}
	}


	// Insert value methods ---------------------------------------------------------------------------------------------
	/**
	 * Check if string contains any insert value
	 * @param {string} string - String to check
	 * @return {boolean}
	 */
	containsInsertValue(string) {
		const {insertValueType, insertValueTypeOptions} = this.props;
		switch (insertValueType) {
			case INSERT_VALUE_BUTTON_TYPE.DIALOG:
				/** @type {InsertValueDialogSectionDataObject[]} */
				const sections = getArray(insertValueTypeOptions, 'dialogProps.sections');
				for (let i of sections) if (i.contains(string)) return true;
				return false;

			case INSERT_VALUE_BUTTON_TYPE.DROPDOWN:
				// TODO: dropdown insert value
				return false;

			default:
				return false;
		}
	}
	

	// Render methods ---------------------------------------------------------------------------------------------------
	render() {
		const {
			className, classNamePrefix, value, defaultValue, placeholder, isMulti, onChange, formControlStyle, isDisabled, 
			isClearable, showInsertValueButton, insertValueButtonProps, insertValueType, insertValueTypeOptions,
			menuRenderInLayout, styles, valueFormat, multiLimit, isLeapYear, ...otherProps
		} = this.props;
		const hasMultiLimit = (isset(multiLimit) && multiLimit !== null);
		const multiLimitNumber = getNumber(multiLimit);
		const selectedOptionsCount = getNumber(this.getSelectedOption(value), 'length');
		const multiLimitHit = (isMulti && hasMultiLimit && selectedOptionsCount >= multiLimitNumber);

		// Prepare portal related props
		let portalProps = {};
		if (menuRenderInLayout) {
			const element = this.getDomElement();
			const fontSize = (element ? getComputedStyle(this.getDomElement()).getPropertyValue('font-size') : '');
			portalProps.styles = (
				styles ?
					{...styles, menuPortal: base => ({ ...base, zIndex: 9999, fontSize })} :
					{menuPortal: base => ({ ...base, zIndex: 9999, fontSize })}
			);
			portalProps.menuPortalTarget = document.querySelector(main_layout_element);
			portalProps.menuPlacement = 'auto';
		}
		
		// Override default CreatableSelect components 
		let components = {Menu: YearDaySelectInputMenuComponent};
		// Hide select arrow for multi select if number of selected options hit the limit
		if (multiLimitHit) set(components, 'DropdownIndicator', () => null);
		
		return (
			<>
				{
					!isDisabled && showInsertValueButton ?
						<InsertValueButton
							buttonProps={{
								displayStyle: BUTTON_STYLE.NONE,
								className: 'input-toolbar-button',
								...insertValueButtonProps
							}}
							insertType={insertValueType}
							insertTypeOptions={insertValueTypeOptions}
							parentSelector={`.${this.getDomId()} .select-input__control`}
							onInsert={v => {
								if (isMulti) {
									if (multiLimitHit) {
										this.onChange([{label: v, value: v}]);	
									} else {
										this.onChange([...getArray(this.getSelectedOption(value)), {label: v, value: v}]);
									}
								} else {
									this.onChange({label: v, value: v});
								}
							}}
							onDialogClose={() => {
								if (!!this.element) {
									this.element.focus();
									if (isMulti) this.element.onMenuOpen();
								}
							}}
						/>
						: null
				}
				
				<CreatableSelect
					inputId={`${this.getDomId()}-input`}
					id={this.getDomId()}
					className={
						`select-input-component ` +
						`${this.getOption('domPrefix')} ${className} ${formControlStyle ? 'form-control' : ''} ` +
						`${this.getDomId()}`
					}
					classNamePrefix={`select-input${classNamePrefix ? ' ' + classNamePrefix : ''}`}
					value={this.getSelectedOption(value)}
					defaultValue={this.getSelectedOption(defaultValue)}
					placeholder={placeholder || placeholder === '' ? placeholder : this.t('Select ...')}
					isDisabled={isDisabled}
					isClearable={isClearable}
					isMulti={isMulti}
					menuIsOpen={multiLimitHit ? false : undefined}
					isSearchable={false}
					components={components}
					menuProps={{valueFormat, isLeapYear}}
					minMenuHeight={250}
					onChange={this.onChange}
					onKeyDown={this.onKeyDown}
					{...portalProps}
					{...omit(otherProps, ['inputId', 'id', 'labelFormat', 'onChange', 'onKeyDown'])}
					ref={node => this.element = node}
				/>
			</>
		);
	}
}

/**
 * Define component's own props that can be passed to it by parent components
 */
YearDaySelectInput.propTypes = {
	// The id to set on the SelectContainer component
	id: PropTypes.string,
	// Apply a className to the control
	className: PropTypes.string,
	// Apply classNames to inner elements with the given prefix
	classNamePrefix: PropTypes.string,
	// Flag that specifies if leap year will be used for the calendar
	isLeapYear: PropTypes.bool,
	// Disable the control
	isDisabled: PropTypes.bool,
	// Allow the user to select multiple values
	isMulti: PropTypes.bool,
	// Maximal number of options that cna be selected for multi select
	multiLimit: PropTypes.number,
	// Flag that determines if input will have a standard form control style
	formControlStyle: PropTypes.bool,
	// Placeholder for the select value
	placeholder: PropTypes.string,
	// Control the current value
	value: PropTypes.any,
	// Set the initial value of the control
	defaultValue: PropTypes.any,
	// Is the select value clearable
	isClearable: PropTypes.bool,
	// Is the select in a state of loading (async)
	isLoading: PropTypes.bool,
	// Flag that determines if menu will be rendered in main layout element (see 'main_layout_element' config value) 
	// using a portal
	menuRenderInLayout: PropTypes.bool,
	// Value date format used to parse the date
	valueFormat: PropTypes.string,
	// Label date format used to render selected values
	labelFormat: PropTypes.string,

	// Flag that determines if insert value button component (InsertValueButton) will be rendered inside the input
	showInsertValueButton: PropTypes.bool,
	// Insert value button props
	insertValueButtonProps: PropTypes.shape({
		// Button element 'id' attribute.
		id: PropTypes.string,
		// Button element CSS class attribute.
		className: PropTypes.string,
		// The default behavior of the button. Possible values are: 'submit', 'reset' or 'button'.
		type: PropTypes.string,
		// Button display type ('none', 'solid', 'transparent', ...)
		displayType: PropTypes.oneOf(BUTTON_DISPLAY_TYPES),
		// Button display style ('default', 'success', 'error', ...)
		displayStyle: PropTypes.oneOf(BUTTON_STYLES),
		// If true, bigger button will be rendered.
		big: PropTypes.bool,
		// The name of the button, submitted as a pair with the button’s value as part of the form data.
		name: PropTypes.string,
		// Defines the value associated with the button’s name when it’s submitted with the form data. This value is 
		// passed to the server in params when the form is submitted.
		value: PropTypes.string,
		// This Boolean attribute specifies that the button should have input focus when the page loads. 
		// @note Only one element in a document can have this attribute.
		autofocus: PropTypes.bool,
		// This Boolean attribute prevents the user from interacting with the button: it cannot be pressed or focused.
		disabled: PropTypes.bool,
		// If true, button will not be rendered.
		hide: PropTypes.bool,
		// Button label rendered as a child of the <button> component before any other child elements but after the icon.
		label: PropTypes.string,
		// Set to true to support HTML in 'label' prop.
		// @warning Be careful when using this flag because it can cause security issues. It uses 'dangerouslySetInnerHTML' 
		// to allow HTML content. 
		allowHtmlLabel: PropTypes.bool,
		// Font icon symbol name.
		icon: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
		// If true, icon will spin (if supported by font icon set used).
		spinIcon: PropTypes.bool,
		// Icon props
		// @see Icon component
		iconProps: PropTypes.object,

		// Events
		onClick: PropTypes.func,
	}),
	// Insert type
	insertValueType: PropTypes.oneOf(INSERT_VALUE_BUTTON_TYPES),
	// Insert type options
	// @note Options depend on 'insertValueType'.
	insertValueTypeOptions: PropTypes.oneOfType([
		// INSERT_VALUE_BUTTON_TYPE_DIALOG
		PropTypes.shape({
			dialogProps: PropTypes.object,
			dialogOptions: PropTypes.shape({
				id: PropTypes.string,
				className: PropTypes.string,
				closeOnEscape: PropTypes.bool,
				closeOnClickOutside: PropTypes.bool,
				hideCloseBtn: PropTypes.bool,
				maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
			}),
		}),
		// INSERT_VALUE_BUTTON_TYPE_DROPDOWN
		PropTypes.shape({
			// TODO: dropdown insert value
		})
	]),

	// Events
	onChange: PropTypes.func, // Arguments: selected option simple value
	onEnterKey: PropTypes.func, // Arguments: keypress event
};

/**
 * Define component default values for own props
 */
YearDaySelectInput.defaultProps = {
	id: '',
	className: '',
	classNamePrefix: '',
	isDisabled: false,
	isMulti: false,
	formControlStyle: true,
	isClearable: false,
	isLoading: false,
	menuRenderInLayout: true,
	valueFormat: 'M-d',
	labelFormat: 'do MMM',
	showInsertValueButton: false,
};

export default YearDaySelectInput;